import React, { useState } from 'react';
import {
  ModalView,
  withTranslation,
  Stack,
  Button,
  ButtonAppearance,
} from '@piwikpro/ui-components';
import { withInject } from '@piwikpro/platform';
import { Confirmation } from '../../Confirmation';

export interface CustomConfirmProps {
  name?: string
  title?: string
  applyBtnText?: string
  applyBtnAppearance?: ButtonAppearance
  onCancel: () => void
  onSubmit: (event: React.SyntheticEvent) => void
  onError?: (error: unknown) => void
  asyncAction?: () => Promise<void>
  t: (key: string, variables?: any) => string
  confirmation: Confirmation
}

export const CustomConfirm: React.FC<CustomConfirmProps> = ({
  name,
  title,
  applyBtnText,
  applyBtnAppearance,
  onSubmit,
  onCancel,
  onError = () => {},
  asyncAction,
  t,
  confirmation,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (event: React.SyntheticEvent) => {
    event.preventDefault();

    if (asyncAction) {
      setIsSubmitting(true);
      try {
        await asyncAction();
        onSubmit(event);
      } catch (error) {
        // Modal stays open on error, hide loader and propagate error
        setIsSubmitting(false);
        onError(error);
      }
    } else {
      onSubmit(event);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onCancel();
    }
  };

  let body: React.ReactNode = null;
  const customBody = confirmation.getCustomBody();
  if (customBody) {
    body = React.createElement(customBody);
  }

  return (
    <ModalView
      title={t(title || '', { name })}
      actions={(
        <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
          <Button
            type="button"
            text={t('components:cancel')}
            appearance="default"
            onClick={onCancel}
            disabled={isSubmitting}
          />
          <Button
            type="submit"
            text={applyBtnText ? t(applyBtnText) : t('profile:deleteConfirm.modal.submit')}
            appearance={applyBtnAppearance || 'destructive'}
            onClick={handleSubmit}
            pending={isSubmitting}
            disabled={isSubmitting}
            focused
          />
        </Stack>
      )}
      onClose={handleClose}
    >
      <form onSubmit={handleSubmit}>
        {body}
      </form>
    </ModalView>
  );
};

export default withInject({
  confirmation: 'ConfirmationCrate.confirmation',
})(withTranslation(['profile', 'components'])(CustomConfirm));
